import React, { useCallback, useRef, useState } from 'react';
import {
  Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Slider, Typography, Alert,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5MB before crop
const OUTPUT_SIZE = 512;                // cropped square exported at 512x512 JPEG

/**
 * Profile-picture upload with a square/circle crop preview.
 *
 * The circular crop mask mirrors exactly how the picture appears in search results
 * and queue pages (Avatar). On confirm the cropped square is exported as a base64
 * JPEG data-URL and handed to `onChange` — the backend stores it and auto-generates
 * the small thumbnail used by list views.
 *
 * Props:
 *   value     – current picture (base64 data-URL or URL) or ''.
 *   onChange  – (base64DataUrl) => void, called after crop confirm.
 *   size      – preview avatar size in px (default 88).
 *   label     – button label (default based on whether a picture exists).
 *   disabled  – disable the change button.
 */
export default function ImageCropUpload({ value = '', onChange, size = 88, label = null, disabled = false }) {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [rawImage, setRawImage] = useState(null);   // data-URL of the freshly picked file
  const [cropOpen, setCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [working, setWorking] = useState(false);

  const pickFile = () => {
    setError('');
    if (inputRef.current) inputRef.current.click();
  };

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    // Allow re-selecting the same file later.
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(jpeg|png)$/i.test(file.type)) {
      setError('Please choose a JPG or PNG image.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('Image is larger than 5MB. Please choose a smaller file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropOpen(true);
    };
    reader.onerror = () => setError('Could not read that file. Please try another image.');
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const confirmCrop = async () => {
    if (!rawImage || !croppedAreaPixels) return;
    setWorking(true);
    try {
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = rawImage;
      });
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        img,
        croppedAreaPixels.x, croppedAreaPixels.y,
        croppedAreaPixels.width, croppedAreaPixels.height,
        0, 0, OUTPUT_SIZE, OUTPUT_SIZE,
      );
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      onChange && onChange(dataUrl);
      setCropOpen(false);
      setRawImage(null);
    } catch (err) {
      setError('Could not crop that image. Please try another one.');
      setCropOpen(false);
    }
    setWorking(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Avatar
          src={value || undefined}
          sx={{ width: size, height: size, bgcolor: 'rgba(0,196,204,0.12)', color: 'primary.main' }}
        >
          {!value && <BusinessOutlinedIcon sx={{ fontSize: size * 0.45 }} />}
        </Avatar>
        <Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoCameraOutlinedIcon />}
            onClick={pickFile}
            disabled={disabled}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {label || (value ? 'Change photo' : 'Upload photo')}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            JPG or PNG, up to 5MB. Shown in search results and queue pages.
          </Typography>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png" hidden onChange={onFile} />

      <Dialog open={cropOpen} onClose={() => !working && setCropOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Crop your photo</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            This circle is exactly how your photo will appear in search results.
          </Typography>
          <Box sx={{ position: 'relative', width: '100%', height: 280, bgcolor: '#111', borderRadius: 2, overflow: 'hidden' }}>
            {rawImage && (
              <Cropper
                image={rawImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Zoom</Typography>
            <Slider size="small" min={1} max={4} step={0.05} value={zoom} onChange={(_, v) => setZoom(v)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCropOpen(false)} disabled={working} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmCrop} disabled={working} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {working ? <CircularProgress size={18} color="inherit" /> : 'Use photo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
